import type { SheetData } from 'write-excel-file/universal';
import { triggerBlobDownload } from '../lib/download';
import { XLSX_BOLD, XLSX_FONT, xlsxSheetName } from '../lib/xlsx';
import type { ExcelDataCell } from './dataTableFormat';

export interface IDataTableExportArgs {
    fileName: string;
    sheetName?: string;
    headers: string[];
    /** Подписи групп колонок параллельно `headers` (пусто — колонка вне групп). */
    headerGroups?: (string | undefined)[];
    /** Строки данных (columnExcelCell: текст — уже отформатированный, числа и даты — значением);
     *  `{ group }` — строка-заголовок группы на всю ширину. */
    rows: (ExcelDataCell[] | { group: string })[];
}

/** Объединение ячеек листа: строки и столбцы с 1, включительно. */
type ExcelMerge = [top: number, left: number, bottom: number, right: number];

export interface IExcelHeaderLayout {
    rows: string[][];
    merges: ExcelMerge[];
}

/**
 * Шапка листа. С группами колонок — две строки, как на экране: подпись группы
 * объединена над своими колонками, а заголовок колонки вне групп занимает обе
 * строки. Подпись «Группа: колонка» в одной строке ломала бы фильтры и ширины
 * колонок Excel длинными заголовками и повторяла группу в каждой колонке.
 */
export function buildExcelHeader(headers: string[], headerGroups?: (string | undefined)[]): IExcelHeaderLayout {
    if (!headerGroups?.some(Boolean)) return { rows: [headers], merges: [] };
    const merges: ExcelMerge[] = [];
    const top = headers.map((header, i) => headerGroups[i] || header);
    for (let i = 0; i < headers.length;) {
        const label = headerGroups[i];
        let end = i;
        while (label && end + 1 < headers.length && headerGroups[end + 1] === label) end++;
        if (!label) merges.push([1, i + 1, 2, i + 1]);
        else if (end > i) merges.push([1, i + 1, 1, end + 1]);
        i = end + 1;
    }
    return { rows: [top, headers], merges };
}

/** Имя без расширения браузер сохраняет по-разному: как есть или со своим расширением. */
const xlsxFileName = (name: string): string => (/\.xlsx$/i.test(name) ? name : `${name}.xlsx`);

const columnWidth = (text: string) => Math.max(14, Math.min(50, text.length + 6));

const GROUPED_HEADER = { ...XLSX_BOLD, align: 'center', alignVertical: 'center', wrap: true } as const;

function headerSheetRows({ rows, merges }: IExcelHeaderLayout): SheetData {
    if (rows.length === 1) return [rows[0].map((value) => ({ value, ...XLSX_BOLD }))];
    const sheetRows: SheetData = rows.map((values) => values.map((value) => ({ value, ...GROUPED_HEADER })));
    merges.forEach(([top, left, bottom, right]) => {
        for (let r = top; r <= bottom; r++) {
            for (let c = left; c <= right; c++) sheetRows[r - 1][c - 1] = null;
        }
        sheetRows[top - 1][left - 1] = {
            value: rows[top - 1][left - 1],
            ...GROUPED_HEADER,
            columnSpan: right - left + 1,
            rowSpan: bottom - top + 1,
        };
    });
    return sheetRows;
}

/** Клиентский экспорт в .xlsx. Библиотека записи нужна только по клику
 *  экспорта — импорт динамический, чтобы чанк не грузился с каждой таблицей.
 *  Отказ (не загрузился чанк, не собралась книга) отклоняет промис: о нём сообщает вызывающий. */
export async function exportDataTableToExcel({
    fileName,
    sheetName,
    headers,
    headerGroups,
    rows,
}: IDataTableExportArgs): Promise<void> {
    const { default: writeXlsxFile } = await import('write-excel-file/universal');
    const data: SheetData = headerSheetRows(buildExcelHeader(headers, headerGroups));
    rows.forEach((row) => {
        if (Array.isArray(row)) data.push(headers.map((_, i) => row[i] ?? ''));
        else data.push([{ value: row.group, ...XLSX_BOLD, columnSpan: Math.max(1, headers.length) }]);
    });
    const blob = await writeXlsxFile(
        data,
        { sheet: xlsxSheetName(sheetName), columns: headers.map((text) => ({ width: columnWidth(text) })) },
        XLSX_FONT,
    ).toBlob();
    triggerBlobDownload(blob, xlsxFileName(fileName));
}
